import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  BarChart3, Eye, Heart, MessageCircle, Repeat2, TrendingUp, RefreshCw, Send, Clock, Mail, Loader2, Eye as EyeIcon,
} from "lucide-react";

const METRIC_KEYS = [
  { keys: ["impressions", "views", "reach"], label: "Impressions", icon: Eye },
  { keys: ["likes", "reactions", "favorites"], label: "Likes", icon: Heart },
  { keys: ["comments", "replies"], label: "Comments", icon: MessageCircle },
  { keys: ["shares", "retweets", "reposts"], label: "Shares", icon: Repeat2 },
];

const pick = (obj, keys) => {
  for (const k of keys) if (obj && obj[k] != null) return obj[k];
  return null;
};

const OverviewCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="rounded-xl border border-border bg-card p-5" data-testid={testid}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className="text-3xl font-display font-bold">{value}</div>
  </div>
);

export default function AnalyticsPage() {
  const { active } = useWorkspace();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    if (!active) return;
    setLoading(true);
    api.get(`/analytics?workspace_id=${active.id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);

  const openPreview = async () => {
    try {
      const r = await api.get("/insights/preview");
      setPreview(r.data.html || "<p style='color:#9CA3AF;font-family:sans-serif;padding:20px'>Nothing to show yet.</p>");
    } catch (e) { toast.error("Could not load preview."); }
  };

  const sendInsight = async () => {
    setSending(true);
    try {
      const r = await api.post("/insights/send");
      if (r.data.sent) toast.success("Weekly insights emailed to you.");
      else toast.error(`Not sent: ${r.data.reason || "unknown"}`);
    } catch (e) { toast.error("Send failed."); }
    finally { setSending(false); }
  };

  if (!active) return null;

  const overview = data?.overview || {};
  const posts = data?.posts || [];
  const lastSync = overview.lastSync ? new Date(overview.lastSync).toLocaleString() : "Not synced yet";

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground mt-1">Live impressions & engagement pulled from Zernio</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="insights-preview-button" onClick={openPreview} variant="ghost" className="text-primary">
            <EyeIcon className="h-4 w-4 mr-2" /> Preview insights
          </Button>
          <Button data-testid="insights-send-button" onClick={sendInsight} disabled={sending} variant="outline" className="border-accent/40 text-accent">
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />} Email me a preview
          </Button>
          <Button data-testid="analytics-refresh-button" onClick={load} variant="outline" className="border-border">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <OverviewCard icon={Send} label="Published" value={data?.published_count ?? 0} accent="bg-emerald-500/15 text-emerald-400" testid="analytics-published" />
        <OverviewCard icon={BarChart3} label="Zernio posts" value={overview.publishedPosts ?? 0} accent="bg-primary/15 text-primary" testid="analytics-zernio-posts" />
        <OverviewCard icon={Clock} label="Scheduled" value={overview.scheduledPosts ?? 0} accent="bg-amber-500/15 text-amber-400" testid="analytics-scheduled" />
        <OverviewCard icon={TrendingUp} label="Total tracked" value={overview.totalPosts ?? 0} accent="bg-accent/15 text-accent" testid="analytics-total" />
      </div>

      <p className="text-xs text-muted-foreground font-mono">Last Zernio sync: {lastSync}</p>

      <div className="rounded-xl border border-border bg-card p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Published posts</h3>
        {loading ? (
          <div className="py-12 text-center text-muted-foreground">Loading analytics…</div>
        ) : posts.length === 0 ? (
          <div className="py-16 text-center text-muted-foreground" data-testid="analytics-empty">
            <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p className="max-w-sm mx-auto mb-4">No published posts yet. Once you publish, Zernio starts collecting impressions and engagement here.</p>
            <Button data-testid="analytics-go-studio" onClick={() => navigate("/studio")} className="bg-primary hover:bg-primary/90 text-white">
              Create your first post
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="analytics-post-list">
            {posts.map((p) => (
              <div key={p.id} className="rounded-lg border border-border bg-secondary/20 p-4" data-testid={`analytics-post-${p.id}`}>
                <div className="flex items-start gap-4">
                  {p.image_path && (
                    <img src={`${API}/media/${p.image_path}`} alt="" className="h-16 w-16 rounded-lg object-cover border border-border shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <Badge className="bg-primary/15 text-primary border-primary/30 capitalize text-[10px]">{p.platform}</Badge>
                      {p.angle_used && <Badge variant="outline" className="border-border text-[10px]">{p.angle_used}</Badge>}
                      <span className="text-[11px] font-mono text-muted-foreground ml-auto">
                        {p.published_at ? new Date(p.published_at).toLocaleDateString() : ""}
                      </span>
                    </div>
                    <p className="text-sm text-foreground/90 line-clamp-2 mb-3">{p.post_text}</p>
                    {p.metrics ? (
                      <div className="flex flex-wrap gap-4">
                        {METRIC_KEYS.map(({ keys, label, icon: Icon }) => {
                          const v = pick(p.metrics, keys);
                          if (v == null) return null;
                          return (
                            <span key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`metric-${label.toLowerCase()}-${p.id}`}>
                              <Icon className="h-3.5 w-3.5 text-primary" /> <span className="font-mono text-foreground">{v}</span> {label}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic">Awaiting first metrics sync from Zernio…</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden" data-testid="insights-preview-dialog">
          <DialogHeader>
            <DialogTitle>Your weekly insights</DialogTitle>
            <DialogDescription>Exactly how your Monday recap email will look.</DialogDescription>
          </DialogHeader>
          <iframe title="insights-preview" srcDoc={preview || ""} className="w-full h-[60vh] rounded-lg border border-border bg-white" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
